import { Text } from "@visx/text";
import { withParentSize } from "@visx/responsive";
import { formatLargeNumber, ordinalSuffix } from "../../util/numberFormats";
import { Group } from "@visx/group";
import { hatch } from "ldrs";

hatch.register();

const { colors } = require("../../util/colors.js");

function getMblueForPercentile(percentile) {
  if (percentile >= 80) return colors.mblue[900];
  if (percentile >= 60) return colors.mblue[700];
  if (percentile >= 40) return colors.mblue[500];
  if (percentile >= 20) return colors.mblue[400];
  return colors.mblue[200];
}

const CoreStatBox = ({
  label,
  value,
  height,
  width,
  parentHeight,
  parentWidth,
  ratio = 0.5,
  valueFontSize = "3rem",
  labelFontSize = null,
  dataLabelFormatter = null,
  loading = false,
  isPercentage = false,
  percentile = null,
  showPercentileBar = false,
}) => {
  if (!dataLabelFormatter) {
    if (isPercentage) {
      dataLabelFormatter = (d) => `${(d * 100).toFixed(0)}%`;
    } else {
      dataLabelFormatter = formatLargeNumber;
    }
  }

  // Set the width and height
  height = height || parentHeight;
  width = width || parentWidth;

  const svgContent = (
    <svg height={height} width={width} className="">
      <Text
        x={width * 0.9}
        y={height * ratio}
        width={width * 0.8}
        textAnchor="end"
        verticalAnchor="end"
        scaleToFit={"shrink-only"}
        className="font-inter fill-mcharcoal-900"
        style={{ fontSize: valueFontSize }}
      >
        {dataLabelFormatter(value)}
      </Text>
      <Text
        x={width * 0.9}
        y={height * ratio + height * 0.1}
        width={width * 0.7}
        textAnchor="end"
        verticalAnchor="start"
        className="font-inter fill-mcharcoal-500 text-lg"
        style={{ fontSize: labelFontSize }}
      >
        {label}
      </Text>
    </svg>
  );

  if (showPercentileBar && typeof percentile === "number") {
    return (
      <div className="flex flex-col items-center w-full">
        {svgContent}
        <div className="flex flex-col items-center mt-1 w-full px-2">
          <div className="w-4/5 mx-auto">
            <div className="flex w-full justify-between text-xs text-slate-400 font-inter mb-0">
              <span>0th</span>
              <span>100th</span>
            </div>
            <div className="w-full h-2.5 rounded-full bg-slate-200 relative overflow-hidden mt-1">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 shadow-sm `}
                style={{
                  width: `${percentile}%`,
                  background: getMblueForPercentile(percentile),
                }}
              />
            </div>
          </div>
          <div className="w-full flex justify-center mt-1">
            <span className="text-slate-600 text-xs text-center font-inter">
              {ordinalSuffix(percentile)} percentile within state
            </span>
          </div>
        </div>
      </div>
    );
  }

  return svgContent;
};

const StatBox = withParentSize(CoreStatBox);

const CoreStatBoxes = ({
  chartData,
  height,
  width,
  parentHeight,
  parentWidth,
  ratio = 0.5,
  labelFontSize = null,
  valueFontSize = "3rem",
  dataLabelFormatter = null,
  loading = false,
  isPercentage = false,
  showPercentileBar = false,
}) => {
  height = height || parentHeight;
  width = width || parentWidth;

  const n = chartData.length;

  // Don't calculate width if there's no data yet
  if (n === 0) {
    return null;
  }

  width = width / n;

  return width < 10 || height < 10 ? null : (
    <>
      {loading && (
        <div className="relative">
          <div
            className={`w-full h-[${height}px] flex items-center justify-center absolute`}
          >
            <l-hatch size="32" color="#334155"></l-hatch>
          </div>
        </div>
      )}
      {chartData && (
        <svg height={height} width={width * n}>
          {chartData.map((stat, index) => {
            const border = index === 0 ? "" : "border-l";

            return (
              <Group key={index} left={width * index} className={border}>
                <CoreStatBox
                  label={stat.label}
                  value={stat.value}
                  height={height}
                  width={width}
                  ratio={ratio}
                  labelFontSize={labelFontSize}
                  valueFontSize={valueFontSize}
                  dataLabelFormatter={dataLabelFormatter}
                  loading={loading}
                  isPercentage={isPercentage}
                  percentile={stat.percentile}
                  showPercentileBar={showPercentileBar}
                />
              </Group>
            );
          })}
        </svg>
      )}
    </>
  );
};

const StatBoxes = withParentSize(CoreStatBoxes);

export { StatBox, StatBoxes };
